import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api, endpoints } from '../../config/api';
import { Spinner } from '../../components/ui';
import './SsoCallbackPage.css';
import { homePathFor } from '../../components/ProtectedRoute';
import { useT, pick, apiErrorMessage } from '../../i18n';

// Mirrors the `reason`/`error` strings organizations/sso_login.py's
// microsoft_callback redirects with (see its module docstring).
const ERROR_MESSAGES = {
  expired: { tr: 'Giriş isteğinin süresi doldu, lütfen tekrar deneyin.', en: 'The sign-in request expired, please try again.' },
  declined: { tr: 'Microsoft girişi iptal edildi veya reddedildi.', en: 'Microsoft sign-in was cancelled or declined.' },
  invalid_token: { tr: 'Microsoft kimlik doğrulaması başarısız oldu, lütfen tekrar deneyin.', en: 'Microsoft authentication failed, please try again.' },
  no_email: { tr: 'Microsoft hesabınızda kullanılabilir bir e-posta adresi bulunamadı.', en: 'No usable email address was found on your Microsoft account.' },
  no_account_or_domain: { tr: 'Bu e-posta adresiyle ilişkili bir organizasyon bulunamadı. Lütfen yöneticinizle iletişime geçin.', en: 'No organization is linked to this email address. Please contact your administrator.' },
  inactive: { tr: 'Hesabınız devre dışı bırakılmış.', en: 'Your account has been deactivated.' },
  tenant_mismatch: { tr: 'Bu Microsoft hesabı, e-posta adresinin bağlı olduğu şirket hesabından gelmiyor. Şirket hesabınızla giriş yapın.', en: 'This Microsoft account does not belong to the company tenant linked to this email address. Sign in with your company account.' },
  license_invalid: { tr: 'Organizasyonunuzun lisansı geçerli değil.', en: 'Your organization’s license is not valid.' },
  invite_invalid: { tr: 'Davet bağlantısı geçersiz veya süresi dolmuş.', en: 'The invitation link is invalid or has expired.' },
  invite_email_mismatch: { tr: 'Farklı bir Microsoft hesabıyla giriş yaptınız — davet başka bir e-postaya gönderilmişti.', en: 'You signed in with a different Microsoft account — the invitation was sent to another email address.' },
  not_configured: { tr: 'Microsoft ile giriş şu anda kullanılamıyor.', en: 'Microsoft sign-in is not available right now.' },
  token_request_failed: { tr: 'Microsoft ile bağlantı kurulamadı, lütfen tekrar deneyin.', en: 'Couldn’t reach Microsoft, please try again.' },
  // Self-service trial (organizations/signup.py)
  email_unverified: { tr: 'Microsoft bu hesabın e-posta adresini doğrulamıyor. Şirketinizin Microsoft 365 iş hesabıyla tekrar deneyin.', en: 'Microsoft doesn’t verify this account’s email address. Try again with your company’s Microsoft 365 work account.' },
  personal_account: { tr: 'Kişisel Microsoft hesaplarıyla deneme açılamaz. Şirketinizin Microsoft 365 iş hesabıyla giriş yapın.', en: 'A trial can’t be opened with a personal Microsoft account. Sign in with your company’s Microsoft 365 work account.' },
  public_domain: { tr: 'Deneme için şirket e-posta adresi gerekiyor (Gmail, Outlook.com gibi adresler kabul edilmez).', en: 'A company email address is required for a trial (addresses like Gmail or Outlook.com aren’t accepted).' },
  org_exists: {
    tr: ({ hint }) => `Şirketinizin zaten bir Martinci hesabı var.${hint ? ` Yöneticiniz (${hint})` : ' Yöneticiniz'} sizi panelden davet edebilir.`,
    en: ({ hint }) => `Your company already has a Martinci account.${hint ? ` Your administrator (${hint})` : ' Your administrator'} can invite you from the dashboard.`,
  },
  signup_limited: { tr: 'Bu bağlantıdan bugün çok fazla deneme açıldı. Lütfen yarın tekrar deneyin ya da bize yazın.', en: 'Too many trials were opened from this connection today. Please try again tomorrow or contact us.' },
};

// Reasons that come back from the /basla trial flow: send the visitor back there.
const SIGNUP_REASONS = new Set(['email_unverified', 'personal_account', 'public_domain', 'org_exists', 'signup_limited']);

const messages = {
  failed: { tr: 'Microsoft ile giriş başarısız oldu.', en: 'Microsoft sign-in failed.' },
  invalidRequest: { tr: 'Geçersiz giriş isteği.', en: 'Invalid sign-in request.' },
  loginFailed: { tr: 'Giriş başarısız oldu, lütfen tekrar deneyin.', en: 'Sign-in failed, please try again.' },
  backToLogin: { tr: 'Giriş sayfasına dön', en: 'Back to sign-in' },
  backToTrial: { tr: 'Deneme sayfasına dön', en: 'Back to the trial page' },
  notCustomer: { tr: 'Şirketiniz henüz Martinci kullanmıyor mu?', en: 'Your company isn’t on Martinci yet?' },
  startTrial: { tr: 'Ücretsiz deneyin →', en: 'Start a free trial →' },
  signingIn: { tr: 'Microsoft ile giriş yapılıyor...', en: 'Signing in with Microsoft...' },
};

export const SsoCallbackPage = () => {
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const { t } = useT(messages);
  // Guards against React StrictMode's dev-only double effect invocation
  // (mount -> cleanup -> mount again): the fragment is cleared via
  // history.replaceState on the first run, so a naive second run would read
  // an already-empty hash and clobber the real result with "invalid
  // request". Never an issue in production (StrictMode's double-invoke is
  // dev-only), but the effect should be correct regardless of how many
  // times it's invoked, not rely on that.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;

    // The exchange code (and any error reason) travels in the URL
    // *fragment*, never the query string — see sso_login.py's module
    // docstring for why (it must never reach a server/proxy access log).
    // URLSearchParams decodes percent-escaping for us.
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const code = params.get('code');
    const errorReason = params.get('error');
    window.history.replaceState(null, '', window.location.pathname);

    if (errorReason) {
      setReason(errorReason);
      setError(ERROR_MESSAGES[errorReason]
        ? pick(ERROR_MESSAGES[errorReason], { hint: params.get('hint') || '' })
        : t('failed'));
      return;
    }
    if (!code) {
      setError(t('invalidRequest'));
      return;
    }

    api.post(endpoints.auth.ssoMicrosoftExchange, { code })
      .then((data) => {
        setSession(data.user, data.tokens);
        navigate(homePathFor(data.user.role), { replace: true });
      })
      .catch((err) => {
        setError(apiErrorMessage(err, messages.loginFailed));
      });
    // Runs once on mount — the fragment is only ever meaningful on the
    // initial landing from Microsoft's redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="sso-callback-page">
      <div className="sso-callback-card">
        {error ? (
          <>
            <p className="sso-callback-error">{error}</p>
            {SIGNUP_REASONS.has(reason)
              ? <Link to="/basla" className="sso-callback-back-link">{t('backToTrial')}</Link>
              : <Link to="/login" className="sso-callback-back-link">{t('backToLogin')}</Link>}
            {reason === 'no_account_or_domain' && (
              <p className="sso-callback-hint">
                {t('notCustomer')}{' '}
                <Link to="/basla" className="sso-callback-back-link">{t('startTrial')}</Link>
              </p>
            )}
          </>
        ) : (
          <>
            <Spinner />
            <p className="sso-callback-message">{t('signingIn')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default SsoCallbackPage;
